import { useQueryClient } from '@tanstack/react-query';
import type { AuthMeResponse } from '@zrsystem/contracts';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useOptionalNavigationContext } from '@/app/navigation-context/use-navigation-context.js';
import { useAuth } from '@/app/providers/auth-provider.js';
import { resolveWorkspaceActivePlace } from '@/app/workspace/resolve-workspace-active-place.js';
import {
  ACTIVE_PLACE_STORAGE_KEY,
  type PlaceSummary,
  type WorkspaceContextValue,
} from '@/app/workspace/types.js';
import { WorkspaceContext } from '@/app/workspace/use-workspace.js';
import { setActivePlaceId } from '@/shared/workspace/active-place-bridge.js';

interface WorkspaceProviderProps {
  children: ReactNode;
}

function buildPlacesFromUser(user: AuthMeResponse): PlaceSummary[] {
  if (!user.placeId) {
    return [];
  }

  return [
    {
      id: user.placeId,
      name: 'Unidade principal',
    },
  ];
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { status, user } = useAuth();
  const navigation = useOptionalNavigationContext();
  const queryClient = useQueryClient();
  const [activePlace, setActivePlaceState] = useState<PlaceSummary | null>(null);
  const allowedPlaces = navigation?.allowedPlaces;
  const navigationLevel = navigation?.level ?? null;
  const navigationEntityId = navigation?.activeEntityId ?? null;

  const places = useMemo(() => {
    if (allowedPlaces && allowedPlaces.length > 0) {
      return allowedPlaces;
    }
    return user ? buildPlacesFromUser(user) : [];
  }, [allowedPlaces, user]);

  useEffect(() => {
    const resolved = resolveWorkspaceActivePlace(places, {
      status,
      navigationLevel,
      navigationEntityId,
      storedPlaceId: localStorage.getItem(ACTIVE_PLACE_STORAGE_KEY),
    });
    setActivePlaceState(resolved);
    setActivePlaceId(resolved?.id ?? null);
    if (resolved) {
      localStorage.setItem(ACTIVE_PLACE_STORAGE_KEY, resolved.id);
    }
  }, [navigationEntityId, navigationLevel, places, status]);

  const setActivePlace = useCallback(
    (placeId: string) => {
      const next = places.find((place) => place.id === placeId);
      if (!next) {
        return;
      }

      localStorage.setItem(ACTIVE_PLACE_STORAGE_KEY, next.id);
      setActivePlaceState(next);
      setActivePlaceId(next.id);
      void queryClient.invalidateQueries();
    },
    [places, queryClient],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      places,
      activePlace,
      setActivePlace,
      isReady:
        status !== 'loading' &&
        (status === 'unauthenticated' ||
          navigation?.level !== 'place' ||
          activePlace !== null ||
          places.length === 0),
    }),
    [activePlace, navigation?.level, places, setActivePlace, status],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
